/*
 * Copyright 2025 Intuition Robotics ltd
 */
import * as React from 'react';
import {CSSProperties} from 'react';
import * as emotion from 'emotion';
import {css} from 'emotion';
import {COLORS} from "@res/colors";

const default_font = css(
	{
		display: 'inline-block',
		fontFamily: 'Inter-Regular',
		fontSize: '12px',
		fontWeight: 'normal',
		fontStyle: 'normal',
		fontStretch: 'normal',
		lineHeight: 'normal',
		letterSpacing: 'normal',
		color: 'black'
	});

type FontFamily = 'Inter-Black'
	| 'Inter-Bold'
	| 'Inter-ExtraBold'
	| 'Inter-ExtraLight'
	| 'Inter-Medium'
	| 'Inter-Regular'
	| 'Inter-SemiBold'
	| 'Inter-Thin'
	| 'Inter-Light';

type FontSize = 'normal' | 11 | 12 | 14 | 15 | 16 | 18 | 20 | 21 | 22 | 23 | 24 | 26 | 60

function Font(props: { children: string, fontFamily?: FontFamily, fontSize?: FontSize, color?: string, fontWeight?: number, style?: CSSProperties, className?: string }) {
	const {fontFamily, fontSize, fontWeight, color, className} = props;

	return <span
		className={`${default_font} ${className || ""}`}
		style={{
			...{
				fontFamily,
				fontSize,
				fontWeight,
				color
			},
			...props.style
		}}>
		{props.children}
	</span>;
}

type FontFn = (text: string, styleOverwrite?: Omit<CSSProperties, 'fontSize'>, className?: string) => React.JSX.Element

// For autocompleting purposes
function asFonts<T extends { [key: string]: FontFn }>(arg: T): T {
	return arg;
}

export const FONTS = asFonts(
	{
		medium_24_blue: (t: string, s?: Omit<CSSProperties, 'fontSize'>) => {
			return <Font style={s} fontFamily={'Inter-Medium'} fontSize={24} color={COLORS.primary_dark_blue}>{t}</Font>;
		},
		reg_14_blue: (t: string, s?: Omit<CSSProperties, 'fontSize'>) => {
			return <Font style={s} fontFamily={'Inter-Regular'} fontSize={14} color={COLORS.primary_dark_blue}>{t}</Font>;
		},
		medium_21: (t: string, s?: Omit<CSSProperties, 'fontSize'>) => {
			return <Font style={s} fontFamily={'Inter-Medium'} fontSize={21} color={COLORS.neutral_text_dark}>{t}</Font>;
		},
		medium_18: (t: string, s?: Omit<CSSProperties, 'fontSize'>) => {
			return <Font style={s} fontFamily={'Inter-Medium'} fontSize={18} color={COLORS.neutral_text_dark}>{t}</Font>;
		},
		medium_18_blue: (t: string, s?: Omit<CSSProperties, 'fontSize'>) => {
			return <Font style={s} fontFamily={'Inter-Medium'} fontSize={18} color={COLORS.primary_dark_blue}>{t}</Font>;
		},
		medium_18_white: (t: string, s?: Omit<CSSProperties, 'fontSize'>) => {
			return <Font style={s} fontFamily={'Inter-Medium'} fontSize={18} color={COLORS.white}>{t}</Font>;
		},
		medium_18_graysdark: (t: string, s?: Omit<CSSProperties, 'fontSize'>) => {
			return <Font style={s} fontFamily={'Inter-Medium'} fontSize={18} color={COLORS.grays_dark}>{t}</Font>;
		},
		medium_18_neutral_gray: (t: string, s?: Omit<CSSProperties, 'fontSize'>) => {
			return <Font style={s} fontFamily={'Inter-Medium'} fontSize={18} color={COLORS.neutral_gray_1}>{t}</Font>;
		},
		reg_18: (t: string, s?: Omit<CSSProperties, 'fontSize'>) => {
			return <Font style={s} fontFamily={'Inter-Regular'} fontSize={18} color={COLORS.neutral_text_dark}>{t}</Font>;
		},
		reg_18_bold: (t: string, s?: Omit<CSSProperties, 'fontSize'>) => {
			return <Font style={s} fontFamily={'Inter-Bold'} fontSize={18} color={COLORS.neutral_text_dark}>{t}</Font>;
		},
		reg_16_black: (t: string, s?: Omit<CSSProperties, 'fontSize'>) => {
			return <Font style={s} fontFamily={'Inter-Regular'} fontSize={16} color={COLORS.black}>{t}</Font>;
		},
		reg_16_primary_dark_blue: (t: string, s?: Omit<CSSProperties, 'fontSize'>) => {
			return <Font style={s} fontFamily={'Inter-Regular'} fontSize={16} color={COLORS.primary_dark_blue}>{t}</Font>;
		},
		reg_14: (t: string, s?: Omit<CSSProperties, 'fontSize'>) => {
			return <Font style={s} fontFamily={'Inter-Regular'} fontSize={14} color={COLORS.neutral_text_dark}>{t}</Font>;
		},
		reg_12: (t: string, s?: Omit<CSSProperties, 'fontSize'>) => {
			return <Font style={s} fontFamily={'Inter-Regular'} fontSize={12} color={COLORS.neutral_text_dark}>{t}</Font>;
		},
		reg_12_bold: (t: string, s?: Omit<CSSProperties, 'fontSize'>) => {
			return <Font style={s} fontFamily={'Inter-Bold'} fontSize={11} color={COLORS.neutral_text_dark}>{t}</Font>;
		},
		medium_12: (t: string, s?: Omit<CSSProperties, 'fontSize'>) => {
			return <Font style={s} fontFamily={'Inter-Medium'} fontSize={12} color={COLORS.neutral_text_dark}>{t}</Font>;
		},
		medium_14: (t: string, s?: Omit<CSSProperties, 'fontSize'>) => {
			return <Font style={s} fontFamily={'Inter-Medium'} fontSize={14} color={COLORS.neutral_text_dark}>{t}</Font>;
		},
		medium_14_white: (t: string, s?: Omit<CSSProperties, 'fontSize'>) => {
			return <Font style={s} fontFamily={'Inter-Medium'} fontSize={14} color={COLORS.white}>{t}</Font>;
		},
		bold_14_white: (t: string, s?: Omit<CSSProperties, 'fontSize'>) => {
			return <Font style={s} fontFamily={'Inter-Bold'} fontSize={14} color={COLORS.white}>{t}</Font>;
		},
		bold_14_blue: (t: string, s?: Omit<CSSProperties, 'fontSize'>) => {
			return <Font style={s} fontFamily={'Inter-Bold'} fontSize={14} color={COLORS.primary_dark_blue}>{t}</Font>;
		},
		reg_14_lightblue: (t: string, s?: Omit<CSSProperties, 'fontSize'>) => {
			return <Font style={s} fontFamily={'Inter-Regular'} fontSize={14} color={COLORS.primary_light_blue}>{t}</Font>;
		},
		reg_14_graysdark: (t: string, s?: Omit<CSSProperties, 'fontSize'>) => {
			return <Font style={s} fontFamily={'Inter-Regular'} fontSize={14} color={COLORS.grays_dark}>{t}</Font>;
		},
		reg_14_neutral_gray: (t: string, s?: Omit<CSSProperties, 'fontSize'>) => {
			return <Font style={s} fontFamily={'Inter-Regular'} fontSize={14} color={COLORS.neutral_gray_1}>{t}</Font>;
		},
		reg_14_neutral_text_dark: (t: string, s?: Omit<CSSProperties, 'fontSize'>) => {
			return <Font style={s} fontFamily={'Inter-Regular'} fontSize={14} color={COLORS.neutral_text_dark}>{t}</Font>;
		},
		reg_14_neutral_text_dark_semi_bold: (t: string, s?: Omit<CSSProperties, 'fontSize'>) => {
			return <Font style={s} fontFamily={'Inter-SemiBold'} fontSize={14} color={COLORS.neutral_text_dark}>{t}</Font>;
		},
		reg_14_secondary_dark: (t: string, s?: Omit<CSSProperties, 'fontSize'>) => {
			return <Font style={s} fontFamily={'Inter-Regular'} fontSize={14} color={COLORS.neutral_text_dark_2}>{t}</Font>;
		},
		reg_14_green: (t: string, s?: Omit<CSSProperties, 'fontSize'>) => {
			return <Font style={s} fontFamily={'Inter-Regular'} fontSize={14} color={COLORS.secondary_green}>{t}</Font>;
		},
		reg_14_red: (t: string, s?: Omit<CSSProperties, 'fontSize'>) => {
			return <Font style={s} fontFamily={'Inter-Regular'} fontSize={14} color={COLORS.secondary_red}>{t}</Font>;
		},
		reg_14_orange: (t: string, s?: Omit<CSSProperties, 'fontSize'>) => {
			return <Font style={s} fontFamily={'Inter-Regular'} fontSize={14} color={COLORS.orange}>{t}</Font>;
		},
		semi_bold_14: (t: string, s?: Omit<CSSProperties, 'fontSize'>, className?: string) => {
			return <Font className={className} style={s} fontFamily={'Inter-SemiBold'} fontSize={14} color={COLORS.neutral_text_dark}>{t}</Font>;
		},
		bold_14_secondary_red: (t: string, s?: Omit<CSSProperties, 'fontSize'>) => {
			return <Font style={s} fontFamily={'Inter-Bold'} fontSize={14} color={COLORS.secondary_red}>{t}</Font>;
		},
		bold_14_secondary_green: (t: string, s?: Omit<CSSProperties, 'fontSize'>) => {
			return <Font style={s} fontFamily={'Inter-Bold'} fontSize={14} color={COLORS.secondary_green}>{t}</Font>;
		},
		bold_14_orange: (t: string, s?: Omit<CSSProperties, 'fontSize'>) => {
			return <Font style={s} fontFamily={'Inter-Bold'} fontSize={14} color={COLORS.orange}>{t}</Font>;
		},
		medium_16_blue: (t: string, s?: Omit<CSSProperties, 'fontSize'>) => {
			return <Font style={s} fontFamily={'Inter-Medium'} fontSize={16} color={COLORS.primary_dark_blue}>{t}</Font>;
		},
		bold_11_secondary_red: (t: string, s?: Omit<CSSProperties, 'fontSize'>) => {
			return <Font style={s} fontFamily={'Inter-Bold'} fontSize={11} color={COLORS.secondary_red}>{t}</Font>;
		},
		reg_11: (t: string, s?: Omit<CSSProperties, 'fontSize'>) => {
			return <Font style={s} fontFamily={'Inter-Regular'} fontSize={11} color={COLORS.neutral_text_dark}>{t}</Font>;
		},
		reg_60: (t: string, s?: Omit<CSSProperties, 'fontSize'>) => {
			return <Font style={s} fontFamily={'Inter-Regular'} fontSize={60} color={COLORS.neutral_text_dark}>{t}</Font>;
		},
		light_11: (t: string, s?: Omit<CSSProperties, 'fontSize'>) => {
			return <Font style={s} fontFamily={'Inter-Light'} fontSize={11} color={COLORS.neutral_text_dark}>{t}</Font>;
		},
		light_12: (t: string, s?: Omit<CSSProperties, 'fontSize'>) => {
			return <Font style={s} fontFamily={'Inter-Light'} fontSize={12} color={COLORS.neutral_text_dark}>{t}</Font>;
		},
		light_21: (t: string, s?: Omit<CSSProperties, 'fontSize'>) => {
			return <Font style={s} fontFamily={'Inter-Light'} fontSize={21} color={COLORS.neutral_text_dark}>{t}</Font>;
		},
		light_23: (t: string, s?: Omit<CSSProperties, 'fontSize'>) => {
			return <Font style={s} fontFamily={'Inter-Light'} fontSize={23} color={COLORS.neutral_text_dark}>{t}</Font>;
		},
		black_11: (t: string, s?: Omit<CSSProperties, 'fontSize'>) => {
			return <Font style={s} fontFamily={'Inter-Black'} fontSize={11} color={COLORS.neutral_text_dark}>{t}</Font>;
		},
		black_23: (t: string, s?: Omit<CSSProperties, 'fontSize'>) => {
			return <Font style={s} fontFamily={'Inter-Black'} fontSize={23} color={COLORS.neutral_text_dark}>{t}</Font>;
		},
		black_18: (t: string, s?: Omit<CSSProperties, 'fontSize'>) => {
			return <Font style={s} fontFamily={'Inter-Black'} fontSize={18} color={COLORS.neutral_text_dark}>{t}</Font>;
		},
		medium_11: (t: string, s?: Omit<CSSProperties, 'fontSize'>) => {
			return <Font style={s} fontFamily={'Inter-Medium'} fontSize={11} color={COLORS.neutral_text_dark}>{t}</Font>;
		},
		reg_11_bold: (t: string, s?: Omit<CSSProperties, 'fontSize'>) => {
			return <Font style={s} fontFamily={'Inter-Bold'} fontSize={11} color={COLORS.neutral_text_dark}>{t}</Font>;
		},
		reg_11_secondary_dark: (t: string, s?: Omit<CSSProperties, 'fontSize'>) => {
			return <Font style={s} fontFamily={'Inter-Regular'} fontSize={11} color={COLORS.neutral_text_dark_2}>{t}</Font>;
		},
		reg_11_white: (t: string, s?: Omit<CSSProperties, 'fontSize'>) => {
			return <Font style={s} fontFamily={'Inter-Regular'} fontSize={11} color={COLORS.white}>{t}</Font>;
		},
		medium_16: (t: string, s?: Omit<CSSProperties, 'fontSize'>) => {
			return <Font style={s} fontFamily={'Inter-Medium'} fontSize={16} color={COLORS.neutral_text_dark}>{t}</Font>;
		},
		medium_16_secondary_red: (t: string, s?: Omit<CSSProperties, 'fontSize'>) => {
			return <Font style={s} fontFamily={'Inter-Medium'} fontSize={16} color={COLORS.secondary_red}>{t}</Font>;
		},
		medium_16_white: (t: string, s?: Omit<CSSProperties, 'fontSize'>) => {
			return <Font style={s} fontFamily={'Inter-Medium'} fontSize={16} color={COLORS.white}>{t}</Font>;
		},
		medium_16_primary_blue: (t: string, s?: Omit<CSSProperties, 'fontSize'>) => {
			return <Font style={s} fontFamily={'Inter-Medium'} fontSize={16} color={COLORS.primary_blue}>{t}</Font>;
		},
		medium_16_secondary_dark: (t: string, s?: Omit<CSSProperties, 'fontSize'>) => {
			return <Font style={s} fontFamily={'Inter-Regular'} fontSize={16} color={COLORS.neutral_text_dark_2}>{t}</Font>;
		},
		reg_16: (t: string, s?: Omit<CSSProperties, 'fontSize'>) => {
			return <Font style={s} fontFamily={'Inter-Regular'} fontSize={16} color={COLORS.neutral_text_dark}>{t}</Font>;
		},
		reg_16_neutral_gray: (t: string, s?: Omit<CSSProperties, 'fontSize'>) => {
			return <Font style={s} fontFamily={'Inter-Regular'} fontSize={16} color={COLORS.neutral_gray_1}>{t}</Font>;
		},
		reg_16_secondary_red: (t: string, s?: Omit<CSSProperties, 'fontSize'>) => {
			return <Font style={s} fontFamily={'Inter-Regular'} fontSize={16} color={COLORS.secondary_red}>{t}</Font>;
		},
		bold_16: (t: string, s?: Omit<CSSProperties, 'fontSize'>) => {
			return <Font style={s} fontFamily={'Inter-Bold'} fontSize={16} color={COLORS.neutral_text_dark}>{t}</Font>;
		},
		bold_15: (t: string, s?: Omit<CSSProperties, 'fontSize'>) => {
			return <Font style={s} fontFamily={'Inter-Bold'} fontSize={15} color={COLORS.neutral_text_dark}>{t}</Font>;
		},
		bold_60: (t: string, s?: Omit<CSSProperties, 'fontSize'>) => {
			return <Font style={s} fontFamily={'Inter-Bold'} fontSize={60} color={COLORS.neutral_text_dark}>{t}</Font>;
		},
		semi_bold_16: (t: string, s?: Omit<CSSProperties, 'fontSize'>) => {
			return <Font style={s} fontFamily={'Inter-SemiBold'} fontSize={16} color={COLORS.neutral_text_dark}>{t}</Font>;
		},
		bold_16_secondary_red: (t: string, s?: Omit<CSSProperties, 'fontSize'>) => {
			return <Font style={s} fontFamily={'Inter-Bold'} fontSize={16} color={COLORS.secondary_red}>{t}</Font>;
		},
		bold_16_white: (t: string, s?: Omit<CSSProperties, 'fontSize'>) => {
			return <Font style={s} fontFamily={'Inter-Bold'} fontSize={16} color={COLORS.white}>{t}</Font>;
		},
		reg_16_secondary_dark: (t: string, s?: Omit<CSSProperties, 'fontSize'>) => {
			return <Font style={s} fontFamily={'Inter-Regular'} fontSize={16} color={COLORS.neutral_text_dark_2}>{t}</Font>;
		},
		reg_16_dark: (t: string, s?: Omit<CSSProperties, 'fontSize'>) => {
			return <Font style={s} fontFamily={'Inter-Regular'} fontSize={16} color={COLORS.black}>{t}</Font>;
		},
		reg_14_gray: (t: string, s?: Omit<CSSProperties, 'fontSize'>) => {
			return <Font style={s} fontFamily={'Inter-Regular'} fontSize={14} color={COLORS.gray_4}>{t}</Font>;
		},
		reg_lightgray_16_L: (t: string, s?: Omit<CSSProperties, 'fontSize'>) => {
			return <Font style={s} fontFamily={'Inter-Regular'} fontSize={16} color={COLORS.neutral_gray_1}>{t}</Font>;
		},
		reg_12_gray: (t: string, s?: Omit<CSSProperties, 'fontSize'>) => {
			return <Font style={s} fontFamily={'Inter-Regular'} fontSize={12} color={COLORS.neutral_gray_5}>{t}</Font>;
		},
		bold_16_primary_blue: (t: string, s?: Omit<CSSProperties, 'fontSize'>) => {
			return <Font style={s} fontFamily={'Inter-Bold'} fontSize={16} color={COLORS.primary_blue}>{t}</Font>;
		},
		bold_16_gray: (t: string, s?: Omit<CSSProperties, 'fontSize'>) => {
			return <Font style={s} fontFamily={'Inter-Bold'} fontSize={16} color={COLORS.neutral_gray_2}>{t}</Font>;
		},
		bold_16_green: (t: string, s?: Omit<CSSProperties, 'fontSize'>) => {
			return <Font style={s} fontFamily={'Inter-Bold'} fontSize={16} color={COLORS.bright_green}>{t}</Font>;
		},
		bold_16_orange_vivid: (t: string, s?: Omit<CSSProperties, 'fontSize'>) => {
			return <Font style={s} fontFamily={'Inter-Bold'} fontSize={16} color={COLORS.orange_vivid}>{t}</Font>;
		},
		bold_16_red: (t: string, s?: Omit<CSSProperties, 'fontSize'>) => {
			return <Font style={s} fontFamily={'Inter-Bold'} fontSize={16} color={COLORS.red}>{t}</Font>;
		},
		bold_14_gray_4: (t: string, s?: Omit<CSSProperties, 'fontSize'>) => {
			return <Font style={s} fontFamily={'Inter-Bold'} fontSize={14} color={COLORS.gray_4}>{t}</Font>;
		},
		bold_22_gray: (t: string, s?: Omit<CSSProperties, 'fontSize'>) => {
			return <Font style={s} fontFamily={'Inter-Bold'} fontSize={22} color={COLORS.neutral_gray_2}>{t}</Font>;
		},
		bold_22_green: (t: string, s?: Omit<CSSProperties, 'fontSize'>) => {
			return <Font style={s} fontFamily={'Inter-Bold'} fontSize={22} color={COLORS.secondary_green}>{t}</Font>;
		},
		bold_22_red: (t: string, s?: Omit<CSSProperties, 'fontSize'>) => {
			return <Font style={s} fontFamily={'Inter-Bold'} fontSize={22} color={COLORS.secondary_red}>{t}</Font>;
		}
	});


// Use this class to avoid UI flickering when changing font
export const reserveTextArea = (t: string, size?: number, family?: 'Regular' | 'Normal' | 'Bold') => emotion.css(
	{
		':after': { // precalculate width/height needed for text with certain font.
			display: 'block',
			content: `"${t}"`,
			fontFamily: `Inter-${family || 'Bold'}`,
			fontSize: `${size || 24}px`,
			height: '1px',
			color: 'transparent',
			overflow: 'hidden',
			visibility: 'hidden'
		}
	});
